import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Env from "@/config/environment";
import type { ConnectorCapability } from "@/features/connectors/types";
import { TEMPORARY_CALLER_NUMBERS_MAX } from "@/features/connectors/types";
import { useTranslation } from "@/features/utils/i18n";
import { Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

interface HeyupNumbersFieldProps {
  selectableNumbers: string[];
  selectedNumbers: string[];
  onToggle: (number: string) => void;
}

export const HeyupNumbersField = ({
  selectableNumbers,
  selectedNumbers,
  onToggle,
}: HeyupNumbersFieldProps) => {
  const { t } = useTranslation("connectors");

  return (
    <div className="space-y-2">
      <Label>
        {t("connectors.heyup.number", "Vos numéros HeyUp")}{" "}
        <span className="text-red-500">*</span>
      </Label>
      <div className="space-y-2 rounded-lg border p-3">
        {/* A list rather than a single pick: a client can order a second
            line, and both land on the same channel — the same agent, the
            same routing rules. A number already attached stays listed even
            if it has since left the attributed set (a request reopened, a
            line moved), so editing the agent never silently drops a line
            the client is answering on. */}
        {selectableNumbers.length === 0 ? (
          <p className="text-xs italic text-muted-foreground">
            {t(
              "connectors.heyup.noNumbers",
              "Aucun numéro ne vous a encore été attribué. Votre demande de numéro est en cours de traitement.",
            )}
          </p>
        ) : (
          selectableNumbers.map((number) => (
            <div key={number} className="flex items-center space-x-2">
              <Checkbox
                id={`heyup-number-${number}`}
                checked={selectedNumbers.includes(number)}
                onCheckedChange={() => onToggle(number)}
              />
              <Label
                htmlFor={`heyup-number-${number}`}
                className="cursor-pointer font-mono text-sm font-normal"
              >
                {number}
              </Label>
            </div>
          ))
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {t(
          "connectors.heyup.numberHint",
          "Les numéros que HeyUp vous a attribués. Aucun identifiant opérateur à saisir : la ligne tourne sur notre compte.",
        )}
      </p>
    </div>
  );
};

interface TwilioCredentialsFieldsProps {
  mode: "create" | "edit";
  capabilities: ConnectorCapability[];
  phoneNumbers: string;
  onPhoneNumbersChange: (value: string) => void;
  accountId: string;
  onAccountIdChange: (value: string) => void;
  authToken: string;
  onAuthTokenChange: (value: string) => void;
  twilioRegion: string;
  onTwilioRegionChange: (value: string) => void;
  whatsappNumbers: string;
  onWhatsappNumbersChange: (value: string) => void;
}

export const TwilioCredentialsFields = ({
  mode,
  capabilities,
  phoneNumbers,
  onPhoneNumbersChange,
  accountId,
  onAccountIdChange,
  authToken,
  onAuthTokenChange,
  twilioRegion,
  onTwilioRegionChange,
  whatsappNumbers,
  onWhatsappNumbersChange,
}: TwilioCredentialsFieldsProps) => {
  const { t } = useTranslation("connectors");

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="phone-numbers">
          {t("connectors.form.phoneNumber")}
        </Label>
        <Input
          id="phone-numbers"
          value={phoneNumbers}
          onChange={(e) => onPhoneNumbersChange(e.target.value)}
          placeholder={t("connectors.form.phoneNumberPlaceholder")}
        />
        <p className="text-xs text-muted-foreground">
          {t("connectors.form.phoneNumberHint")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="account-id">
          {t("connectors.form.accountSid")}{" "}
          <span className="text-red-500">*</span>
        </Label>
        <Input
          id="account-id"
          value={accountId}
          onChange={(e) => onAccountIdChange(e.target.value)}
          placeholder={t("connectors.form.accountSidPlaceholder")}
          required
          autoComplete="off"
        />
        <p className="text-xs text-muted-foreground">
          {t("connectors.form.accountSidHint")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="auth-token">
          {t("connectors.form.authToken")}{" "}
          {mode === "create" && <span className="text-red-500">*</span>}
        </Label>
        <Input
          id="auth-token"
          type="password"
          autoComplete="off"
          value={authToken}
          onChange={(e) => onAuthTokenChange(e.target.value)}
          placeholder={
            mode === "edit"
              ? t("connectors.form.authTokenPlaceholderEdit")
              : t("connectors.form.authTokenPlaceholder")
          }
          required={mode === "create"}
        />
        <p className="text-xs text-muted-foreground">
          {mode === "edit"
            ? t("connectors.form.authTokenHintEdit")
            : t("connectors.form.authTokenHint")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="twilio-region">
          {t("connectors.form.twilioRegion")}
        </Label>
        <Input
          id="twilio-region"
          value={twilioRegion}
          onChange={(e) => onTwilioRegionChange(e.target.value)}
          placeholder={t("connectors.form.twilioRegionPlaceholder")}
          autoComplete="off"
        />
        <p className="text-xs text-muted-foreground">
          {t("connectors.form.twilioRegionHint")}
        </p>
      </div>

      {capabilities.includes("whatsapp") && (
        <div className="space-y-2">
          <Label htmlFor="whatsapp-numbers">
            {t("connectors.form.whatsappNumber")}
          </Label>
          <Input
            id="whatsapp-numbers"
            value={whatsappNumbers}
            onChange={(e) => onWhatsappNumbersChange(e.target.value)}
            placeholder={t("connectors.form.whatsappNumberPlaceholder")}
          />
          <p className="text-xs text-muted-foreground">
            {t("connectors.form.whatsappNumberHint")}
          </p>
        </div>
      )}
    </>
  );
};

/** One read-only URL with the button that copies it. */
const WebhookRow = ({ label, url }: { label: string; url: string }) => {
  const { t } = useTranslation("connectors");
  return (
    <div className="space-y-1">
      <Label className="text-xs font-medium">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          readOnly
          value={url}
          className="text-xs font-mono bg-background"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            navigator.clipboard.writeText(url);
            toast.success(t("connectors.form.copied"));
          }}
        >
          <Copy className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

/**
 * The three URLs to paste into the Twilio console. Nothing to point at Twilio
 * on a HeyUp number: the webhooks are already wired on our own carrier account.
 */
export const TwilioWebhooksPanel = () => {
  const { t } = useTranslation("connectors");

  return (
    <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
      <Label className="text-sm font-semibold">
        {t("connectors.form.webhookTitle")}
      </Label>
      <p className="text-xs text-muted-foreground mb-3">
        {t("connectors.form.webhookDescription")}
      </p>

      <div className="space-y-2">
        <WebhookRow
          label={t("connectors.form.webhookVoice")}
          url={`${Env.server}/api/sessions/v1/twilio/voice`}
        />
        <WebhookRow
          label={t("connectors.form.webhookStatus")}
          url={`${Env.server}/api/sessions/v1/twilio/voice/status`}
        />
        <WebhookRow
          label={t("connectors.form.webhookSms")}
          url={`${Env.server}/api/sessions/v1/twilio/sms`}
        />
      </div>
    </div>
  );
};

interface TemporaryCallerNumbersFieldProps {
  callerNumbers: string;
  onChange: (value: string) => void;
}

export const TemporaryCallerNumbersField = ({
  callerNumbers,
  onChange,
}: TemporaryCallerNumbersFieldProps) => {
  const { t } = useTranslation("connectors");

  return (
    <div className="space-y-2">
      <Label htmlFor="caller-numbers">
        {t("connectors.temporary.callerNumbers", "Vos numéros d'appel")}
      </Label>
      <Input
        id="caller-numbers"
        value={callerNumbers}
        onChange={(e) => onChange(e.target.value)}
        placeholder="+33612345678, +33698765432"
      />
      <p className="text-xs text-muted-foreground">
        {t("connectors.temporary.callerNumbersHint", {
          max: TEMPORARY_CALLER_NUMBERS_MAX,
          defaultValue: `Séparés par des virgules, ${TEMPORARY_CALLER_NUMBERS_MAX} maximum. Quand vous appelez le numéro de démonstration depuis un de ces numéros, c'est l'agent choisi ci-dessus qui répond. Un numéro ne peut être déclaré que par un seul compte. Les transferts et rappels pendant une démo sont limités à ces mêmes numéros.`,
        })}
      </p>
    </div>
  );
};
